import { apiBase } from './apiBase';
import { apiFetch, ApiError } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { TeamUser } from './dailyApi';
import type { PersonProjectAllocation, PersonTask, PersonTaskStats } from './peopleApi';

// User-account endpoints: the chat-roster profile card, plus lifecycle (SaaS
// hardening) — GDPR anonymization and per-user data export. Mirrors
// backend/src/modules/users/users.controller.ts.

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** The card a colleague sees when they click a member in the chat roster. */
export interface UserProfile extends TeamUser {
  isActive: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  person: {
    id: string;
    title: string | null;
    department: string | null;
    location: string | null;
    phone: string | null;
    startDate: string | null;
  } | null;
  /** Open (non-terminal) tasks assigned to the user, priority-first. */
  tasks: PersonTask[];
  /** Projects derived from those tasks, busiest first. */
  projects: PersonProjectAllocation[];
  stats: PersonTaskStats;
}

/** GET /api/users/:id/profile */
export async function getUserProfile(userId: string): Promise<UserProfile> {
  const res = await apiFetch<Envelope<UserProfile>>(
    `/api/users/${encodeURIComponent(userId)}/profile`,
    { headers: authHeaders() },
  );
  return res.data;
}

/**
 * POST /api/users/:id/anonymize — GDPR account "deletion" via anonymization.
 * The account is scrubbed and disabled but the row survives so business FKs
 * (tasks, invoices, payroll) stay intact.
 */
export async function anonymizeUser(
  userId: string,
): Promise<{ userId: string; personId: string | null }> {
  const res = await apiFetch<Envelope<{ userId: string; personId: string | null }>>(
    `/api/users/${userId}/anonymize`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

/**
 * GET /api/users/:id/gdpr-export — download the per-user GDPR ZIP. The route
 * streams a binary attachment, so this bypasses apiFetch's JSON handling
 * (same pattern as the directory Excel export).
 */
export async function downloadGdprExport(userId: string): Promise<void> {
  const res = await fetch(`${apiBase()}/api/users/${userId}/gdpr-export`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    let message = 'Exportul GDPR a eșuat.';
    let code: string | undefined;
    try {
      const parsed = JSON.parse(text) as { error?: { message?: string; code?: string } };
      message = parsed.error?.message ?? message;
      code = parsed.error?.code;
    } catch {
      // non-JSON body — keep the fallback message
    }
    throw new ApiError(res.status, message, code);
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition');
  const match = disposition ? /filename="?([^"]+)"?/i.exec(disposition) : null;
  const filename = match?.[1] ?? `gdpr-export-${userId}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
